import React, { useEffect, useState } from 'react';

import classNames from 'classnames';

import {
  AdPlacement,
  AdServer,
  Brand,
  type ClientAdPlacement,
  debugLog,
  DeviceType,
  type GamPlacement,
  type LayoutType,
  PageType,
  type RecommendationCategories
} from '@schibsted-nmp/advertising-shared';
import { useInitiateGamUnit } from '@client/adManager/AdUnit/useInitiateGamUnit.js';
import { $config } from '@client/core/atoms/config.js';
import { $placementsMap } from '@client/core/atoms/placements.js';
import { observeFullWidthAd } from '@client/core/utils/dom/handleAdsOverlap/observeFullWidthAd.js';
import { setAdnamiPlacement } from '@client/core/utils/dom/handleStickySkyscrapers.js';
import {
  noThreshold,
  SCROLL_MARGIN_TAKEOVER,
  type InViewSettings
} from '@client/core/utils/getInViewSettingsByPlacementId.js';
import { isFinnTravelResultPage } from '@client/core/utils/travel/isFinnTravelResultPage.js';
import { useInScreen } from '@client/core/hooks/useInScreen.js';

export type GamAdUnitProps = {
  placement: ClientAdPlacement<GamPlacement>;
  containerId: string;
  initialLayoutType?: LayoutType;
  categories?: RecommendationCategories;
  inViewSettings?: InViewSettings | undefined;
};

function GamAdUnit({ containerId, placement, inViewSettings }: GamAdUnitProps) {
  const { brand, deviceType, vertical, pageType } = $config.get() ?? {};
  const { placementId } = placement;
  const ref = React.createRef<HTMLDivElement>();

  const isGamLazyLoadingEnabled = !isFinnTravelResultPage();

  const [clientPlacement, setClientPlacement] =
    useState<ClientAdPlacement<GamPlacement>>(placement);

  const { creativeType } = clientPlacement;

  useEffect(() => {
    const unsubscribe = $placementsMap.subscribe((placements) => {
      const clientPlacement =
        typeof placements === 'object' && placements[placement.placementId];

      if (!clientPlacement) return;

      if (clientPlacement.adServer.type === AdServer.Gam) {
        setClientPlacement(clientPlacement as ClientAdPlacement<GamPlacement>);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [placement]);

  const { isIntersecting } = useInitiateGamUnit({
    placement: clientPlacement,
    containerId,
    ref,
    inViewSettings,
    isGamLazyLoadingEnabled
  });

  useEffect(() => {
    if (creativeType === 'adnami') {
      setAdnamiPlacement(placementId);
      return;
    }

    if (
      (brand === Brand.Tori || brand === Brand.Dba) &&
      placementId === AdPlacement.AdvtMiddle2.id &&
      deviceType === DeviceType.Desktop
    ) {
      observeFullWidthAd(placementId, () => {
        debugLog('Full width ad detected');
        setAdnamiPlacement(placementId);
      });
    }
  }, [creativeType, placementId]);

  const isTakeoverAd = creativeType === 'takeover';
  const { isIntersecting: isTakeOverInScreen } = useInScreen({
    ref,
    threshold: noThreshold,
    rootMargin: SCROLL_MARGIN_TAKEOVER
  });
  const takeoverIsIntersecting = isTakeoverAd && isIntersecting;

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const { body } = document;

      if (takeoverIsIntersecting) {
        body.classList.add('advt-takeover-ad');
      } else {
        body.classList.remove('advt-takeover-ad');
      }
    }
  }, [takeoverIsIntersecting]);

  // Sponsored Content & Native ad styling
  const isNativeAdStyles =
    creativeType === 'native' || creativeType === 'sponsored-content';

  const cardNativeAdVertical = ['realestate', 'travel', 'car'].includes(
    vertical ?? ''
  );

  const isRecommendationFrontAd =
    isNativeAdStyles &&
    placementId.includes('recirc') &&
    pageType === PageType.Front;

  const cardNativeAd =
    isNativeAdStyles && (cardNativeAdVertical || isRecommendationFrontAd);

  return (
    <div
      ref={ref}
      className={classNames('flex w-full advt-placement-container', {
        'is-intersecting': isTakeoverAd ? isTakeOverInScreen : isIntersecting,
        'h-full': !isRecommendationFrontAd
      })}
    >
      <div
        className={classNames('mx-auto h-full advt-placement-item', {
          'w-full': isNativeAdStyles,
          'shadow-s hover:shadow-m border border-gray-200 rounded-8':
            cardNativeAd || creativeType === 'sponsored-content'
        })}
        id={placementId}
        data-is-adnami={creativeType === 'adnami'}
      />
    </div>
  );
}

export default GamAdUnit;
